import { 
  Button, 
  Flex, 
  Heading, 
  Text, 
  HStack,
  IconButton,
  Tooltip,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  useBreakpointValue,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  VStack,
  Icon,
  Link,
  MenuGroup,
  Divider,
  useColorMode,
  useMediaQuery,
  Avatar,
  Box,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { FaChevronDown, FaUser, FaCog, FaSignOutAlt, FaTheaterMasks, FaMusic, FaBook, FaFileAlt, FaQuestionCircle, FaHandHoldingUsd, FaExternalLinkAlt, FaSun, FaMoon, FaGamepad, FaExpandAlt, FaHeadphones } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useLyricsTheme } from '../context/LyricsThemeContext';
import { FiMaximize, FiSun, FiMoon } from 'react-icons/fi';

// Add icons for layout options
const SplitLayoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 3h8v18H3V3zm10 0h8v18h-8V3z" />
  </svg>
);

// Define a proper TeslaLayoutIcon component
const TeslaLayoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 3h18v12H3V3zm0 14h18v4H3v-4z" />
  </svg>
);

// Menu icon
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
  </svg>
);

interface AppHeaderProps {
  playbackState?: any;
  layoutMode?: 'split' | 'tesla';
  setLayoutMode?: (mode: 'split' | 'tesla') => void;
  accentColor?: string | undefined | null;
  centerComponent?: React.ReactNode;
  leftComponent?: React.ReactNode;
  pageTitle?: string;
  isCondensed?: boolean;
}

const AppHeader = ({
  playbackState = null,
  layoutMode = 'split',
  setLayoutMode = () => {},
  accentColor,
  centerComponent,
  leftComponent,
  pageTitle,
  isCondensed = false,
}: AppHeaderProps) => {
  const { user, signOut } = useAuth();
  const { colorMode, setColorMode } = useLyricsTheme();
  const isAuthenticated = !!user;
  const username = user?.email?.split('@')[0] || null;
  const location = useLocation();
  const navigate = useNavigate();

  // For mobile menu
  const { isOpen: isMobileMenuOpen, onOpen: onMobileMenuOpen, onClose: onMobileMenuClose } = useDisclosure();
  
  // Use smaller header on scroll
  const [scrolled, setScrolled] = useState(false);

  // Add media query to check if screen width is between 800px and 1200px
  const [isInFullscreenRange, setIsInFullscreenRange] = useState(false);
  
  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      // Only track scrolling of the main document, not component containers
      // Check if scrollY is a good indicator - if we're in a component with its own scroll container,
      // the window.scrollY might not change much
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    // Use passive: true for better performance with scroll events
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Effect to check if viewport width is between 800px and 1200px
  useEffect(() => {
    const checkWidth = () => {
      const width = window.innerWidth;
      setIsInFullscreenRange(width >= 800 && width <= 1200);
    };
    
    checkWidth(); // Check on mount
    window.addEventListener('resize', checkWidth);
    
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Function to handle fullscreen browser redirection
  const openFullscreenBrowser = () => {
    const currentUrl = window.location.href;
    // Redirect to YouTube with the current URL
    window.open(`https://www.youtube.com/redirect?q=${encodeURIComponent(currentUrl)}`, '_blank');
  };

  // Toggle between light and dark mode
  const toggleColorMode = () => {
    const newMode = colorMode === 'dark' ? 'light' : 'dark';
    setColorMode(newMode);
    localStorage.setItem('syncdit-theme', newMode);
  };

  
  // User dropdown with profile, settings, and sign out
  const UserDropdown = ({ username, signOut }: { username: string, signOut: () => void }) => {
    return (
      <Menu>
        <MenuButton
          as={Button}
          variant="ghost"
          color="whiteAlpha.900"
          fontWeight="bold"
          fontSize="lg"
          _hover={{ 
            bg: 'whiteAlpha.200',
            color: 'var(--color-primary-variant)'
          }}
          _active={{ 
            bg: 'whiteAlpha.300',
            color: 'var(--color-primary)'
          }}
          transition="all 0.2s ease"
        >
          <Text fontWeight="medium" color="gray.500">
            {username || ''}
          </Text>
        </MenuButton>
        <MenuList minW="200px">
          <MenuGroup title={username || ''}>
            <MenuItem onClick={() => window.location.href = '/settings'}>Settings</MenuItem>
            <MenuItem onClick={signOut}>Sign Out</MenuItem>
          </MenuGroup>
        </MenuList>
      </Menu>
    );
  };

  // Render mobile menu
  const renderMobileMenu = () => (
    <Drawer
      isOpen={isMobileMenuOpen}
      placement="right"
      onClose={onMobileMenuClose}
      size="full"
    >
      <DrawerOverlay />
      <DrawerContent 
        bg="rgba(15, 15, 15, 0.97)" 
        css={{
          backdropFilter: "blur(10px)",
        }}
      >
        <DrawerCloseButton/>
        <DrawerBody p={6}>
          <VStack spacing={4} align="stretch">
            {isAuthenticated ? (
              <>
                <Button
                  variant="solid"
                  colorScheme="red"
                  onClick={() => signOut()}
                  _hover={{ 
                    bg: 'rgba(250, 45, 72, 0.15)',
                    transform: 'translateX(5px)'
                  }}
                  leftIcon={<Icon as={FaSignOutAlt} />}
                  justifyContent="flex-start"
                  fontWeight="medium"
                  transition="all 0.2s ease"
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  colorScheme="blue"
                  onClick={() => window.location.href = '/signup?tab=0'}
                  _hover={{ 
                    bg: 'rgba(66, 153, 225, 0.15)',
                    transform: 'translateX(5px)'
                  }}
                  justifyContent="flex-start"
                  fontWeight="medium"
                  transition="all 0.2s ease"
                >
                  Sign In
                </Button>
                <Button
                  variant="solid"
                  bg="var(--color-primary)"
                  color="white"
                  _hover={{
                    bg: 'var(--color-primary-variant)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(29, 185, 84, 0.3)'
                  }}
                  onClick={() => window.location.href = '/signup?tab=1'}
                  fontWeight="medium"
                  transition="all 0.2s ease"
                >
                  Sign Up
                </Button>
              </>
            )}
            
            <Divider borderColor="rgba(255, 255, 255, 0.1)" my={3} />
            
            <Text color="var(--color-primary-muted)" fontSize="xs" fontWeight="bold" letterSpacing="1px" textTransform="uppercase" mb={1}>
              MAIN LINKS
            </Text>
            <Button
              as={RouterLink}
              to="/player"
              leftIcon={<Icon as={FaMusic} color="var(--color-primary)" boxSize={5} />}
              bg= "rgba(15, 15, 15, 0.97)"
              justifyContent="flex-start"
              _hover={{ 
                bg: 'rgba(29, 185, 84, 0.15)',
                transform: 'translateX(5px)'
              }}
              fontWeight="medium"
              transition="all 0.2s ease"
            >
              Player
            </Button>
            <Button
              as={RouterLink}
              to="/theatre-apps"
              leftIcon={<Icon as={FaTheaterMasks} color="#FA2D48" boxSize={5} />}
              variant="ghost"
              justifyContent="flex-start"
              _hover={{ 
                bg: 'rgba(250, 45, 72, 0.15)',
                transform: 'translateX(5px)'
              }}
              fontWeight="medium"
              transition="all 0.2s ease"
            >
              Theatre
            </Button>
            <Button
              as={RouterLink}
              to="/games"
              leftIcon={<Icon as={FaGamepad} color="#FA2D48" boxSize={5} />}
              variant="ghost"
              justifyContent="flex-start"
              _hover={{ 
                bg: 'rgba(250, 45, 72, 0.15)',
                transform: 'translateX(5px)'
              }}
              fontWeight="medium"
              transition="all 0.2s ease"
            >
              Games
            </Button>
            
            <Divider borderColor="rgba(255, 255, 255, 0.1)" my={3} />
            
            <Text color="var(--color-primary-muted)" fontSize="xs" fontWeight="bold" letterSpacing="1px" textTransform="uppercase" mb={1}>
              PREFERENCES
            </Text>
            <Button
              leftIcon={<Icon as={colorMode === 'dark' ? FaSun : FaMoon} color={colorMode === 'dark' ? 'yellow.300' : 'blue.700'} />}
              onClick={toggleColorMode}
              variant="ghost"
              justifyContent="flex-start"
              _hover={{ 
                bg: colorMode === 'dark' ? 'rgba(250, 240, 137, 0.12)' : 'rgba(74, 85, 104, 0.12)',
                transform: 'translateX(5px)'
              }}
              fontWeight="medium"
              transition="all 0.2s ease"
            >
              {colorMode === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </Button>
            
            <Divider borderColor="rgba(255, 255, 255, 0.1)" my={3} />
            
            <Text color="var(--color-primary-muted)" fontSize="xs" fontWeight="bold" letterSpacing="1px" textTransform="uppercase" mb={1}>
              OTHER LINKS
            </Text>
            <Button
              as="a"
              href="/privacy"
              target="_blank"
              leftIcon={<Icon as={FaFileAlt} color="#4299E1" boxSize={4} />}
              variant="ghost"
              justifyContent="flex-start"
              _hover={{ 
                bg: 'rgba(66, 153, 225, 0.15)',
                transform: 'translateX(5px)'
              }}
              size="sm"
              fontWeight="medium"
              transition="all 0.2s ease"
              mb={1}
            >
              Privacy Policy
            </Button>
            <Button
              as="a"
              href="/terms"
              target="_blank"
              leftIcon={<Icon as={FaBook} color="#4299E1" boxSize={4} />}
              variant="ghost"
              justifyContent="flex-start"
              _hover={{ 
                bg: 'rgba(66, 153, 225, 0.15)',
                transform: 'translateX(5px)'
              }}
              size="sm"
              fontWeight="medium"
              transition="all 0.2s ease"
              mb={1}
            >
              Terms of Service
            </Button>
            <Button
              as="a"
              href="/refund"
              target="_blank"
              leftIcon={<Icon as={FaHandHoldingUsd} color="#4299E1" boxSize={4} />}
              variant="ghost"
              justifyContent="flex-start"
              _hover={{ 
                bg: 'rgba(66, 153, 225, 0.15)',
                transform: 'translateX(5px)'
              }}
              size="sm"
              fontWeight="medium"
              transition="all 0.2s ease"
              mb={1}
            >
              Refund Policy
            </Button>
            {!isAuthenticated && (
              <MenuItem
                as="a"
                href="/pricing"
                target="_blank"
                icon={<Icon as={FaExternalLinkAlt} color="white" boxSize={4} />}
                _hover={{ 
                  bg: 'rgba(56, 178, 172, 0.15)',
                  transform: 'translateX(5px)'
                }}
                fontWeight="medium"
                fontSize="sm"
                padding="0.55rem 1rem"
                transition="all 0.2s ease"
              >
                Pricing
              </MenuItem>
            )}
            <MenuItem
              as="a"
              href="/help"
              target="_blank"
              icon={<Icon as={FaQuestionCircle} color="#4299E1" boxSize={4} />}
              _hover={{ 
                bg: 'rgba(66, 153, 225, 0.15)', 
                transform: 'translateX(5px)'
              }}
              fontWeight="medium"
              fontSize="sm"
              padding="0.55rem 1rem"
              transition="all 0.2s ease"
            >
              Help Center
            </MenuItem>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  // Determine current page based on URL path
  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path.includes('player')) return 'Player';
    if (path.includes('theatre')) return 'Theatre';
    if (path.includes('games')) return 'Games';
    return pageTitle || "SyncdIt";
  };

  // Header actions based on authentication status (desktop)
  const renderActions = () => {
    const { user, userProfile, isSubscriber } = useAuth();
    if (isAuthenticated) {
      return (
        <>
        {!isSubscriber ? (
          <Button
            as="a"
            href="/pricing"
            size={isCondensed ? "md" : "sm"}
            variant="ghost"
            color="white"
            _hover={{ bg: 'rgba(56, 178, 172, 0.15)' }}
          >
            Pricing
          </Button>
        ):(
          <>
          </>
        )}
          {/* Only render the Button if screen width is between 800px and 1200px */}
          {isInFullscreenRange && (
            <Tooltip label="Enter Fullscreen Browser">
              <Button
                aria-label="Enter Fullscreen Browser"
                variant="ghost"
                onClick={openFullscreenBrowser}
                mr={2}
                color="white"
                _hover={{
                  bg: 'rgba(56, 178, 172, 0.15)'
                }}
              >
                <Icon as={FiMaximize} />
                <Text fontWeight="bold" fontSize="sm" px={2}>Fullscreen</Text>
              </Button>
            </Tooltip>
          )}
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                color="whiteAlpha.900"
                fontSize="lg"
                fontWeight="bold"
                transition="all 0.2s ease"
                _hover={{
                  bg: "whiteAlpha.200",
                  color: "var(--color-primary-variant)",
                }}
                _active={{
                  bg: "whiteAlpha.300",
                  color: "var(--color-primary)",
                }}
              >
                <Text color="white" fontWeight="medium">
                  {username || ""}
                </Text>
              </MenuButton>

              <MenuList minW="200px">
                <MenuGroup title={username || ""}>
                  <MenuItem
                    onClick={() => {
                      window.location.href = "/settings";
                    }}
                  >
                    Settings
                  </MenuItem>

                  <MenuItem onClick={signOut}>
                    Sign Out
                  </MenuItem>
                </MenuGroup>
              </MenuList>
            </Menu>
        </>
      );
    } else {
      return (
        <>
          <Button
            as="a"
            href="/pricing"
            size={isCondensed ? "xs" : "sm"}
            variant="ghost"
            colorScheme="teal"
            _hover={{ bg: 'rgba(56, 178, 172, 0.15)' }}
          >
            Pricing
          </Button>
          <Button
            size={isCondensed ? "xs" : "sm"}
            variant="ghost"
            colorScheme="blue"
            onClick={() => navigate('/signup?tab=0')}
            _hover={{ bg: 'whiteAlpha.200' }}
          >
            Login
          </Button>
          <Button
            size={isCondensed ? "xs" : "sm"}
            variant="solid"
            colorScheme="blue"
            onClick={() => navigate('/signup?tab=1')}
          >
            Sign Up
          </Button>
        </>
      );
    }
  };

  const actualAccentColor = accentColor || '#1DB954';

  return (
    <>
      <Flex
        as="header"
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
        bg="rgba(0, 0, 0, 0.8)"
        backdropFilter="blur(10px)"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        height={isCondensed ? "60px" : "80px"}
        align="center"
        px={isCondensed ? 3 : 4}
        transition="all 0.2s ease"
      >
        {/* Left section - Logo */}
        <HStack flex="1" justify="flex-start" width="33%">
          <Link as={RouterLink} to="/" _hover={{ textDecoration: 'none' }}>
            <Flex align="center">
              {/* Desktop logo with text */}
              <Box display={{ base: 'none', md: 'block' }} mr={2}>
                <img 
                  src="/assets/logos/logo_only.png" 
                  alt="SyncdIt Logo"
                  width="28px"
                  height="28px"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const iconElement = document.createElement('span');
                    iconElement.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="#4299E1"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"></path></svg>';
                    e.currentTarget.parentNode?.appendChild(iconElement);
                  }}
                />
              </Box>
              
              {/* Mobile logo icon only */}
              <Box display={{ base: 'block', md: 'none' }} mr={2}>
                <img 
                  src="/assets/logos/logo_only.png" 
                  alt="SyncdIt Logo"
                  width="24px"
                  height="24px"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const iconElement = document.createElement('span');
                    iconElement.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="#4299E1"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"></path></svg>';
                    e.currentTarget.parentNode?.appendChild(iconElement);
                  }}
                />
              </Box>
              
              {/* Brand name instead of logo */}
              <Heading 
                as="h1" 
                size={scrolled || isCondensed ? "md" : "lg"}
                transition="font-size 0.2s ease"
                bgGradient="linear(to-r, blue.400, purple.500)"
                bgClip="text"
                display={{ base: pageTitle ? 'none' : 'block', md: 'block' }}
                ml={pageTitle ? 2 : 0}
              >
                {pageTitle || "SyncdIt"}
              </Heading>
            </Flex>
          </Link>
        </HStack>

        {/* Center section */}
        <HStack flex="1" justify="center" width="33%">
          {centerComponent ? centerComponent : (
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<Icon as={FaChevronDown} boxSize={3} />}
                variant="ghost"
                color="whiteAlpha.900"
                fontWeight="bold"
                fontSize="lg"
                _hover={{ 
                  bg: 'whiteAlpha.200',
                  color: 'var(--color-primary-variant)'
                }}
                _active={{ 
                  bg: 'whiteAlpha.300',
                  color: 'var(--color-primary)'
                }}
                transition="all 0.2s ease"
              >
                {getCurrentPageName()}
              </MenuButton>
              <MenuList 
                bg="rgba(15, 15, 15, 0.95)" 
                borderColor="var(--color-primary-muted)" 
                minW="200px"
                boxShadow="0 4px 12px rgba(0, 0, 0, 0.5)"
                borderRadius="md"
                overflow="hidden"
                padding="0.5rem 0"
                css={{
                  backdropFilter: "blur(10px)",
                }}
              >
                <MenuItem
                  as={RouterLink}
                  to="/player"
                  icon={<Icon as={FaMusic} color="var(--color-primary)" boxSize={5} />}
                  _hover={{ 
                    bg: 'rgba(29, 185, 84, 0.15)',
                    transform: 'translateX(5px)'
                  }}
                  fontWeight="medium"
                  padding="0.65rem 1rem"
                  transition="all 0.2s ease"
                  bg="rgba(15, 15, 15, 0.97)"

                >
                  Player
                </MenuItem>
                <MenuItem
                  as={RouterLink}
                  to="/theatre-apps"
                  icon={<Icon as={FaTheaterMasks} color="#FA2D48" boxSize={5} />}
                  _hover={{ 
                    bg: 'rgba(250, 45, 72, 0.15)',
                    transform: 'translateX(5px)'
                  }}
                  fontWeight="medium"
                  padding="0.65rem 1rem"
                  transition="all 0.2s ease"
                  bg="rgba(15, 15, 15, 0.97)"

                >
                  Theatre
                </MenuItem>
                <MenuItem
                  as={RouterLink}
                  to="/games"
                  icon={<Icon as={FaGamepad} color="#FA2D48" boxSize={5} />}
                  _hover={{ 
                    bg: 'rgba(250, 45, 72, 0.15)',
                    transform: 'translateX(5px)'
                  }}
                  fontWeight="medium"
                  padding="0.65rem 1rem"
                  transition="all 0.2s ease"
                  bg="rgba(15, 15, 15, 0.97)"
                >
                  Games
                </MenuItem>
                <MenuDivider borderColor="rgba(255, 255, 255, 0.1)" margin="0.5rem 0" />
                <MenuGroup 
                  title="Other Links" 
                  color="gray.400" 
                  fontSize="xs" 
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="1px"
                  pl={4} 
                  pt={1} 
                  pb={2}
                >
                  <MenuItem
                    as="a"
                    href="/privacy"
                    target="_blank"
                    icon={<Icon as={FaFileAlt} color="#4299E1" boxSize={4} />}
                    _hover={{ 
                      bg: 'rgba(66, 153, 225, 0.15)',
                      transform: 'translateX(5px)'
                    }}
                    fontWeight="medium"
                    fontSize="sm"
                    padding="0.55rem 1rem"
                    transition="all 0.2s ease"
                    bg="rgba(15, 15, 15, 0.97)"

                  >
                    Privacy Policy
                  </MenuItem>
                  <MenuItem
                    as="a"
                    href="/terms"
                    target="_blank"
                    icon={<Icon as={FaBook} color="#4299E1" boxSize={4} />}
                    _hover={{ 
                      bg: 'rgba(66, 153, 225, 0.15)',
                      transform: 'translateX(5px)'
                    }}
                    fontWeight="medium"
                    fontSize="sm"
                    padding="0.55rem 1rem"
                    transition="all 0.2s ease"
                    bg="rgba(15, 15, 15, 0.97)"

                  >
                    Terms of Service
                  </MenuItem>
                  <MenuItem
                    as="a"
                    href="/refund"
                    target="_blank"
                    icon={<Icon as={FaHandHoldingUsd} color="#4299E1" boxSize={4} />}
                    _hover={{ 
                      bg: 'rgba(66, 153, 225, 0.15)',
                      transform: 'translateX(5px)'
                    }}
                    fontWeight="medium"
                    fontSize="sm"
                    padding="0.55rem 1rem"
                    transition="all 0.2s ease"
                    bg="rgba(15, 15, 15, 0.97)"

                  >
                    Refund Policy
                  </MenuItem>
                  <MenuItem
                    as="a"
                    href="/pricing"
                    target="_blank"
                    icon={<Icon as={FaExternalLinkAlt} color="#38B2AC" boxSize={4} />}
                    _hover={{ 
                      bg: 'rgba(56, 178, 172, 0.15)',
                      transform: 'translateX(5px)'
                    }}
                    fontWeight="medium"
                    fontSize="sm"
                    padding="0.55rem 1rem"
                    transition="all 0.2s ease"
                    bg="rgba(15, 15, 15, 0.97)"

                  >
                    Pricing
                  </MenuItem>
                  <MenuItem
                    as="a"
                    href="/help"
                    target="_blank"
                    icon={<Icon as={FaQuestionCircle} color="#4299E1" boxSize={4} />}
                    _hover={{ 
                      bg: 'rgba(66, 153, 225, 0.15)', 
                      transform: 'translateX(5px)'
                    }}
                    fontWeight="medium"
                    fontSize="sm"
                    padding="0.55rem 1rem"
                    transition="all 0.2s ease"
                    bg="rgba(15, 15, 15, 0.97)"

                  >
                    Help Center
                  </MenuItem>
                </MenuGroup>
              </MenuList>
            </Menu>
          )}
          {leftComponent && leftComponent}
        </HStack>

        {/* Right section - Auth buttons */}
        <HStack flex="1" color="black" justify="flex-end" width="33%" spacing={3}>
          {renderActions()}
        </HStack>
      </Flex>
      
      {/* Mobile Menu */}
      {renderMobileMenu()}
    </>
  );
};

export default AppHeader; 